import {
  FeatureGating,
  OnDeviceCache,
  PaywallDismissal,
  PaywallPresentationStyle,
  PaywallTemplateType,
  PresentationCondition,
  ShowPaywallOption,
  SurveyPresentationCondition,
  ReRouteBackButton,
  GameControllerSupport,
} from "@superwall/db"
import {
  IsScrollEnabled,
  LocalNotificationsSchema,
  PageStyles,
  SubSchema,
} from "@superwall-me/schema"
import { LiquidString } from "app/core/validators"
import { z } from "zod"

export const DisablePaywallOptionReAssign = z.object({
  type: z.literal("re-assign"),
  reassignToPaywallId: z.number(),
})

export const DisablePaywallOptionDoNothing = z.object({
  type: z.literal("do-nothing"),
})

export const DisablePaywallOptionUnassign = z.object({
  type: z.literal("un-assign"),
})

export const DisablePaywallOptions = z.union([
  DisablePaywallOptionReAssign,
  DisablePaywallOptionUnassign,
  DisablePaywallOptionDoNothing,
])

export type EditorSectionName = "main" | "products" | "variables-default" | "variables-free-trial"

export const substitutionSchema = SubSchema.omit({
  value: true,
  freeTrialValue: true,
}).extend({
  value: LiquidString,
  freeTrialValue: LiquidString.optional(),
})

export const Substitutions = z.array(substitutionSchema)

export const Paywall = z.object({
  name: z.string().min(1),
  url: z
    .string()
    .url()
    .transform((urlString) => {
      // Make sure we remove the `transport` params from the url
      // b/c it breaks the sdk.
      try {
        let url = new URL(urlString)
        url.searchParams.delete("transport")
        return url.toString()
      } catch {}
      return urlString
    }),
  primaryProductIdentifier: z.string().nullable().optional(),
  secondaryProductIdentifier: z.string().nullable().optional(),
  tertiaryProductIdentifier: z.string().nullable().optional(),
  presentationCondition: z.nativeEnum(PresentationCondition).optional(),
  presentationStyle: z.nativeEnum(PaywallPresentationStyle).default(PaywallPresentationStyle.MODAL),
  showPaywallOption: z.nativeEnum(ShowPaywallOption).optional(),
  paywallDismissal: z.nativeEnum(PaywallDismissal).optional().default(PaywallDismissal.NORMAL),
  backgroundColorHex: z.string(),
  featureGating: z.nativeEnum(FeatureGating).optional(),
  onDeviceCache: z.nativeEnum(OnDeviceCache).optional(),
  isScrollEnabled: z.nativeEnum(IsScrollEnabled).optional(),
  reRouteBackButton: z.nativeEnum(ReRouteBackButton).optional(),
  gameControllerSupport: z.nativeEnum(GameControllerSupport).optional(),
  // TODO we may want to do this in the future, but for now the FE is handling
  // validation visually for the background color hex as well as page styles grouped styles colors.
  // .regex(/^\#?[0-9a-fA-F]{6}$/, { message: "Color must match # and 6 hex characters" }),
  applicationId: z.number().optional(),
  substitutions: Substitutions,
  pageStyles: PageStyles,
  templateJson: z.object({}).passthrough().nullable().optional(),
  templateType: z
    .union([z.literal(PaywallTemplateType.GLOBAL), z.literal(PaywallTemplateType.ORGANIZATION)])
    .nullable(),
  localNotifications: LocalNotificationsSchema.optional(),
})

export const PaywallFormValidator = Paywall

export const CreatePaywallPipeValidator = Paywall.extend({
  applicationId: z.number(),
  enabled: z.boolean().optional(),
})

export const UpdatePaywallPipeValidator = Paywall.extend({
  id: z.number(),
  enabled: z.boolean().optional(),
  surveys: z
    .array(
      z.object({
        surveyId: z.number(),
        surveyPresentationCondition: z.nativeEnum(SurveyPresentationCondition),
      })
    )
    .optional(),
}).partial()

export enum CloneOptionType {
  AsDuplicate = "clone-as-duplicate",
  AsTemplate = "clone-as-template",
  AsUnlisted = "clone-as-unlisted",
}

export const DuplicatePaywall = z.object({
  id: z.number(),
  name: z.string().optional(),
  targetApplicationId: z.number(),
  cloneOptionType: z.nativeEnum(CloneOptionType).default(CloneOptionType.AsDuplicate),
  templateId: z.string().optional(),
})

export const DeletePaywall = z.object({
  id: z.number(),
})
